import { gql } from '@apollo/client';

import { formatCurrency } from '@/lib/utils';

// Query to get all available preferred investment types
export const GET_ALL_PREFERRED_INVESTMENT_TYPES = gql`
  query GetAllPreferredInvestmentTypes {
    preferredInvestmentTypes {
      id
      name
      isActive
    }
  }
`;

// Query to get all available preferred regions
export const GET_ALL_PREFERRED_REGIONS = gql`
  query GetAllPreferredRegions {
    preferredRegions {
      id
      name
      isActive
    }
  }
`;

// Query to get all available preferred industries
export const GET_ALL_PREFERRED_INDUSTRIES = gql`
  query GetAllPreferredIndustries {
    preferredIndustries {
      id
      name
      isActive
    }
  }
`;

export interface AllPreferredIndustriesData {
  preferredIndustries: PreferenceQueryItem[];
}

export interface AllPreferredInvestmentTypesData {
  preferredInvestmentTypes: PreferenceQueryItem[];
}

export interface AllPreferredRegionsData {
  preferredRegions: PreferenceQueryItem[];
}

// Shared interface for preference items from queries
export interface PreferenceQueryItem {
  id: string;
  isActive: boolean;
  name: string;
}

export const GET_ACCOUNT_DEAL_PREFERENCES_AND_PREFERENCES = gql`
  query GetAccountDealPreferencesAndPreferences($accountId: ID!) {
    account: node(id: $accountId) {
      ... on AccountNode {
        id
        name

        # Preferred Regions associated with this account
        preferredRegionIds {
          id
          name
          isActive
        }

        # Preferred Industries associated with this account
        preferredIndustryIds {
          id
          name
          isActive
        }

        # Preferred Investment Types associated with this account
        preferredInvestmentTypesIds {
          id
          name
          isActive
        }

        dealPreferencesIds {
          id
          accountId {
            id
            name
          }
          partnerId {
            id
            name
          }
          currencyId

          # Revenue preferences
          minRevenue
          minRevenueBase
          maxRevenue
          maxRevenueBase

          # EBITDA preferences
          minEbitda
          minEbitdaBase
          maxEbitda
          maxEbitdaBase

          # Investment Amount preferences
          minInvestmentAmount
          minInvestmentAmountBase
          maxInvestmentAmount
          maxInvestmentAmountBase

          # Deal Size preferences
          minDealSize
          minDealSizeBase
          maxDealSize
          maxDealSizeBase
        }
      }
    }
  }
`;

export interface AccountPreferencesNode {
  dealPreferencesIds?: DealPreferencesNode[] | null;
  id: string;
  name?: null | string;
  preferredIndustryIds?: null | PreferredIndustryNode[];
  preferredInvestmentTypesIds?: null | PreferredInvestmentTypeNode[];
  preferredRegionIds?: null | PreferredRegionNode[];
}

export interface DealPreferencesAccountNode {
  id: string;
  name: string;
}

export interface DealPreferencesNode {
  accountId?: DealPreferencesAccountNode | null;
  currencyId?: null | string;
  id: string;
  maxDealSize?: null | number;
  maxDealSizeBase?: null | number;
  maxEbitda?: null | number;
  maxEbitdaBase?: null | number;
  maxInvestmentAmount?: null | number;
  maxInvestmentAmountBase?: null | number;
  maxRevenue?: null | number;
  maxRevenueBase?: null | number;
  minDealSize?: null | number;
  minDealSizeBase?: null | number;
  minEbitda?: null | number;
  minEbitdaBase?: null | number;
  minInvestmentAmount?: null | number;
  minInvestmentAmountBase?: null | number;
  minRevenue?: null | number;
  minRevenueBase?: null | number;
  partnerId?: DealPreferencesPartnerNode | null;
}

export interface DealPreferencesPartnerNode {
  id: string;
  name: string;
}

export interface GetAccountPreferencesData {
  account: AccountPreferencesNode | null;
}

export interface GetAccountPreferencesVariables {
  accountId: string;
}

// UI Data Types (matching existing structure)
export interface InvestmentPreferencesData {
  directCreditDeals: {
    lpInvestmentAmount: string;
  };
  investmentInterestRows: InvestmentInterestRow[];
  preferredIndustries: PreferenceItem[];
  preferredInvestmentTypes: PreferenceItem[];
  preferredRegions: PreferenceItem[];
  secondaryFunds: {
    lpInvestmentAmount: string;
  };
  targetCompanyPreferences: TargetCompanyPreferences;
}

// Preference item with id and name for API operations
export interface PreferenceItem {
  id: string;
  name: string;
}

export interface PreferredIndustryNode {
  id: string;
  isActive?: boolean | null;
  name: string;
}

export interface PreferredInvestmentTypeNode {
  id: string;
  isActive?: boolean | null;
  name: string;
}

// GraphQL Response Types
export interface PreferredRegionNode {
  id: string;
  isActive?: boolean | null;
  name: string;
}

interface InvestmentInterestRow {
  ioName: string;
  priority: string;
  probability: string;
  proceed: string;
  product: string;
}

interface TargetCompanyPreferences {
  maxAnnualRevenue: string;
  maxAnnualRevenueBase: string;
  maxDealSize: string;
  maxDealSizeBase: string;
  maxEbitda: string;
  maxEbitdaBase: string;
  maxInvestmentAmount: string;
  maxInvestmentAmountBase: string;
  minAnnualRevenue: string;
  minAnnualRevenueBase: string;
  minDealSize: string;
  minDealSizeBase: string;
  minEbitda: string;
  minEbitdaBase: string;
  minInvestmentAmount: string;
  minInvestmentAmountBase: string;
}

export function createEmptyInvestmentPreferencesData(): InvestmentPreferencesData {
  return {
    directCreditDeals: {
      lpInvestmentAmount: ''
    },
    investmentInterestRows: [],
    preferredIndustries: [] as PreferenceItem[],
    preferredInvestmentTypes: [] as PreferenceItem[],
    preferredRegions: [] as PreferenceItem[],
    secondaryFunds: {
      lpInvestmentAmount: ''
    },
    targetCompanyPreferences: {
      maxAnnualRevenue: '',
      maxAnnualRevenueBase: '',
      maxDealSize: '',
      maxDealSizeBase: '',
      maxEbitda: '',
      maxEbitdaBase: '',
      maxInvestmentAmount: '',
      maxInvestmentAmountBase: '',
      minAnnualRevenue: '',
      minAnnualRevenueBase: '',
      minDealSize: '',
      minDealSizeBase: '',
      minEbitda: '',
      minEbitdaBase: '',
      minInvestmentAmount: '',
      minInvestmentAmountBase: ''
    }
  };
}

export function mapAccountPreferences(
  node: AccountPreferencesNode
): InvestmentPreferencesData {
  // Get the first deal preferences entry (or use empty defaults)
  const dealPreferences =
    node.dealPreferencesIds && node.dealPreferencesIds.length > 0
      ? node.dealPreferencesIds[0]
      : null;

  return {
    directCreditDeals: {
      lpInvestmentAmount: '' // Not in query
    },
    investmentInterestRows: [], // Not in query
    preferredIndustries:
      node.preferredIndustryIds
        ?.filter(industry => industry.isActive !== false)
        .map(industry => ({ id: industry.id, name: industry.name })) ?? [],
    preferredInvestmentTypes:
      node.preferredInvestmentTypesIds
        ?.filter(type => type.isActive !== false)
        .map(type => ({ id: type.id, name: type.name })) ?? [],
    preferredRegions:
      node.preferredRegionIds
        ?.filter(region => region.isActive !== false)
        .map(region => ({ id: region.id, name: region.name })) ?? [],
    secondaryFunds: {
      lpInvestmentAmount: '' // Not in query
    },
    targetCompanyPreferences: {
      maxAnnualRevenue: formatCurrency(dealPreferences?.maxRevenue),
      maxAnnualRevenueBase: formatCurrency(dealPreferences?.maxRevenueBase),
      maxDealSize: formatCurrency(dealPreferences?.maxDealSize),
      maxDealSizeBase: formatCurrency(dealPreferences?.maxDealSizeBase),
      maxEbitda: formatCurrency(dealPreferences?.maxEbitda),
      maxEbitdaBase: formatCurrency(dealPreferences?.maxEbitdaBase),
      maxInvestmentAmount: formatCurrency(dealPreferences?.maxInvestmentAmount),
      maxInvestmentAmountBase: formatCurrency(
        dealPreferences?.maxInvestmentAmountBase
      ),
      minAnnualRevenue: formatCurrency(dealPreferences?.minRevenue),
      minAnnualRevenueBase: formatCurrency(dealPreferences?.minRevenueBase),
      minDealSize: formatCurrency(dealPreferences?.minDealSize),
      minDealSizeBase: formatCurrency(dealPreferences?.minDealSizeBase),
      minEbitda: formatCurrency(dealPreferences?.minEbitda),
      minEbitdaBase: formatCurrency(dealPreferences?.minEbitdaBase),
      minInvestmentAmount: formatCurrency(dealPreferences?.minInvestmentAmount),
      minInvestmentAmountBase: formatCurrency(
        dealPreferences?.minInvestmentAmountBase
      )
    }
  };
}

// Format currency helper
// Note: formatCurrency is imported from @/lib/utils
